import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  useAppDispatch,
  useAppSelector,
} from '../../../../App/Hooks/reduxHooks'
import { Person } from '../../../../App/store/features/person/IPerson'
import { Button } from '../../../../Common/Button'
import { Spinner } from '../../../../Common/Spinner'
import { RootState } from '../../../../store'
import { createAppointment } from '../../../store/thunks/createAppointment'
import { fetchAppointment } from '../../../store/thunks/fetchAppointment'
import { IAppointmentFormSubmission } from '../../../types'
import { ICreateEvent } from '../../../types/events'
import { isInstanceOfIThunkError } from '../../../utils/interfaceCheck'
import { RequiredMessages } from '../../RequiredMessages'
import { ICrudActivityDetails } from '../../types/ICrudActivityDetails'
import { AddActivityViewSwitch } from '../AddActivityViewSwitch'
import { createActivitySubmitArray } from '../utils/createActivitySubmitArray'

import { CreateFormFields } from './CreateFormFields'

interface IProps {
  appointmentDetails: ICreateEvent
  personList: Person[]
  setIsPanelFocus: (state: boolean) => void
}

export const CreateAppointment = ({
  appointmentDetails,
  personList,
  setIsPanelFocus,
}: IProps) => {
  const { t } = useTranslation()
  const [selectViewOpen, setSelectViewOpen] = useState<
    'activity' | 'menu' | undefined
  >(undefined)
  const [selectedActivitiesArray, setSelectedActivitiesArray] =
    useState<ICrudActivityDetails>({ data: [], actionType: 'create' })

  const activityList = useAppSelector(
    (state: RootState) => state.activityNew.list,
  )

  const dispatch = useAppDispatch()

  const { handleSubmit, setValue, register, watch, formState } =
    useForm<IAppointmentFormSubmission>({
      mode: 'onSubmit',
      defaultValues: {
        customerUuid: '',
        startsAt: appointmentDetails.startsAt,
        appointmentActivities: [],
        isRepeat: false,
        repeatWeekInterval: 6,
        repeatStopsAt: appointmentDetails.startsAt.clone().add(10, 'weeks'),
        repeatStopsAfter: 10,
        repeatOption: 'stopAfter',
        notes: '',
        startHour: appointmentDetails.startsAt.clone().format('HH:mm'),
      },
    })

  useEffect(() => {
    register('customerUuid', { required: 'Please select a customer.' })
    register('appointmentActivities', { required: 'Please select a activity.' })
  }, [register])

  useEffect(() => {
    if (activityList) {
      setValue(
        'appointmentActivities',
        createActivitySubmitArray(selectedActivitiesArray.data),
      )
    }
  }, [activityList, personList, selectedActivitiesArray, setValue])

  // @ts-ignore
  const watchIsRepeat = watch('isRepeat', false)
  // @ts-ignore
  const watchRepeatOption = watch('repeatOption', 'stopAfter')

  const onSubmit = async (formObject: any) => {
    const { payload } = await dispatch(createAppointment(formObject))

    if (!!payload && !isInstanceOfIThunkError(payload)) {
      dispatch(fetchAppointment({ uuid: payload.uuid, type: 'create' }))
      return null
    }
    return null
  }

  if (!activityList) {
    return <Spinner />
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AddActivityViewSwitch
        selectViewOpen={selectViewOpen}
        setSelectViewOpen={setSelectViewOpen}
        setSelectedActivityArray={setSelectedActivitiesArray}
        selectedActivityArray={selectedActivitiesArray.data}
        activityList={activityList}
        personList={personList}
        defaultPersonUuids={[appointmentDetails.selectedPerson.personUuid]}
      />
      <CreateFormFields
        setIsPanelFocus={setIsPanelFocus}
        isSelectViewOpen={!!selectViewOpen}
        setValue={setValue}
        appointmentMoment={appointmentDetails.startsAt}
        watchIsRepeat={watchIsRepeat}
        watchRepeatOption={watchRepeatOption}
        selectedPersonName={appointmentDetails.selectedPerson.firstName}
        setSelectedActivityArray={setSelectedActivitiesArray}
        selectedActivityArray={selectedActivitiesArray.data}
        setSelectViewOpen={setSelectViewOpen}
      />
      <RequiredMessages errors={formState.errors} />
      <Button>{t('Save')}</Button>
    </form>
  )
}
